import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/layout/Header';
import CreateEditPackageModal from './CreateEditPackageModal';
import { packageService } from '../../services/packageService';
import { Package, formatPrice } from '../../types/package';
import { useToast } from '../../contexts/ToastContext';

/* -- Status Badge ---------------------------------------------- */
const StatusBadge: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const config = isActive
    ? { color: 'text-primary', dotColor: 'bg-primary animate-pulse shadow-lg shadow-primary/50', label: 'Active' }
    : { color: 'text-amber-500', dotColor: 'bg-amber-500', label: 'Inactive' };

  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${config.dotColor}`} />
      <span className={`text-sm font-bold italic ${config.color}`}>{config.label}</span>
    </div>
  );
};

/* -- Package Card ---------------------------------------------- */
const PackageCard: React.FC<{
  pkg: Package;
  onEdit: (pkg: Package) => void;
  onToggleActive: (pkg: Package) => void;
  onDelete: (pkg: Package) => void;
}> = ({ pkg, onEdit, onToggleActive, onDelete }) => {
  return (
    <div
      className={`bg-white rounded-xl p-6 flex flex-col group relative overflow-hidden transition-all border border-slate-200 hover:border-primary/50 ${
        !pkg.isActive ? 'opacity-60' : ''
      }`}
    >
      {/* Sort Order Badge */}
      <div className="absolute top-3 right-3">
        <span className="bg-slate-100 text-slate-500 text-[10px] font-black px-2 py-0.5 rounded border border-slate-200">
          #{pkg.sortOrder}
        </span>
      </div>

      {/* Background Icon */}
      <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none group-hover:opacity-10 transition-opacity">
        <span className="material-symbols-outlined text-8xl">bolt</span>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div
          className={`size-10 rounded-lg flex items-center justify-center transition-colors ${
            pkg.isActive
              ? 'bg-primary/20 text-primary'
              : 'bg-slate-100 text-slate-500'
          }`}
        >
          <span className="material-symbols-outlined">inventory_2</span>
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">{pkg.name}</h2>
          <StatusBadge isActive={pkg.isActive} />
        </div>
      </div>

      {/* Price */}
      <div className="mb-8">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-black text-slate-900">
            {formatPrice(pkg.monthlyPrice, pkg.currency)}
          </span>
          <span className="text-slate-500 text-sm font-bold">/ month</span>
        </div>
        {pkg.description && (
          <p className="text-slate-500 text-xs mt-2 font-medium">{pkg.description}</p>
        )}
      </div>

      {/* Resource Limits */}
      <div className="space-y-6 mb-8">
        {/* Content Limit */}
        <div>
          <div className="flex justify-between text-xs font-bold mb-2">
            <span className="text-slate-500">MONTHLY CONTENT</span>
            <span className="text-slate-900">{pkg.monthlyContentLimit.toLocaleString()} / mo</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden border bg-slate-100 border-slate-200">
            <div
              className="h-full bg-primary rounded-full shadow-sm shadow-primary/50"
              style={{ width: `${Math.min(100, (pkg.monthlyContentLimit / 1000) * 100)}%` }}
            />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
            <span className="block text-[10px] text-slate-500 uppercase font-black mb-1">WP SITES</span>
            <span className="text-lg font-bold text-slate-900">{pkg.maxWordPressSites} Site{pkg.maxWordPressSites > 1 ? 's' : ''}</span>
          </div>
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
            <span className="block text-[10px] text-slate-500 uppercase font-black mb-1">USER SEATS</span>
            <span className="text-lg font-bold text-slate-900">{pkg.maxUsers} Seat{pkg.maxUsers > 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Overage Price */}
        {pkg.overageContentPrice > 0 && (
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
            <span className="block text-[10px] text-slate-500 uppercase font-black mb-1">OVERAGE PRICE</span>
            <span className="text-sm font-bold text-amber-400">
              {formatPrice(pkg.overageContentPrice, pkg.currency)} / extra content
            </span>
          </div>
        )}

        {/* Dynamic Features */}
        <div className="flex flex-wrap gap-1.5">
          {pkg.bulkContentEnabled && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 border border-blue-200 text-blue-600 text-[10px] font-bold">
              <span className="material-symbols-outlined text-[12px]">batch_prediction</span>
              Toplu ×{pkg.maxBulkContentPerRequest}
            </span>
          )}
          {pkg.linkAssistantEnabled && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-50 border border-purple-200 text-purple-600 text-[10px] font-bold">
              <span className="material-symbols-outlined text-[12px]">link</span>
              Link Asistanı
            </span>
          )}
          {(pkg.productionPriority ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-600 text-[10px] font-bold">
              <span className="material-symbols-outlined text-[12px]">bolt</span>
              Priority {pkg.productionPriority}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-6 border-t border-slate-200 mt-auto">
        <button
          onClick={() => onEdit(pkg)}
          className="flex-1 font-bold py-2 rounded-lg text-xs transition-colors flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900"
        >
          <span className="material-symbols-outlined text-base">edit</span>
          Edit
        </button>
        <button
          onClick={() => onToggleActive(pkg)}
          className={`size-8 rounded-lg flex items-center justify-center transition-colors ${
            pkg.isActive
              ? 'bg-slate-100 hover:bg-amber-900/30 text-slate-500 hover:text-amber-500'
              : 'bg-slate-100 hover:bg-emerald-900/30 text-slate-500 hover:text-emerald-500'
          }`}
          title={pkg.isActive ? 'Deactivate' : 'Activate'}
        >
          <span className="material-symbols-outlined text-sm">
            {pkg.isActive ? 'pause_circle' : 'play_circle'}
          </span>
        </button>
        <button
          onClick={() => onDelete(pkg)}
          className="size-8 bg-slate-100 hover:bg-red-900/30 text-slate-500 hover:text-red-500 rounded-lg flex items-center justify-center transition-colors"
          title="Delete"
        >
          <span className="material-symbols-outlined text-sm">delete</span>
        </button>
      </div>
    </div>
  );
};

/* -- Delete Confirm Dialog ------------------------------------- */
const DeleteConfirm: React.FC<{
  pkg: Package;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}> = ({ pkg, onConfirm, onCancel, isDeleting }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onCancel} />
    <div className="relative bg-white border border-slate-200 rounded-2xl p-8 w-full max-w-md text-center">
      <div className="size-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="material-symbols-outlined text-3xl text-red-500">warning</span>
      </div>
      <h3 className="text-xl font-bold mb-2 text-slate-900">Delete Package</h3>
      <p className="text-slate-500 text-sm mb-6">
        Are you sure you want to delete <strong className="text-slate-900">{pkg.name}</strong>?
        This action cannot be undone.
      </p>
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
          disabled={isDeleting}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center justify-center gap-2"
          disabled={isDeleting}
        >
          {isDeleting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Deleting...
            </>
          ) : (
            'Delete'
          )}
        </button>
      </div>
    </div>
  </div>
);

/* -- Packages Page --------------------------------------------- */
const PackagesPage: React.FC = () => {
  const { toast } = useToast();
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState<Package | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchPackages = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await packageService.getAll();
      if (response.success) {
        setPackages(response.data);
      } else {
        setError(response.message || 'Failed to load packages');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load packages');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleCreateClick = () => {
    setEditingPackage(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (pkg: Package) => {
    setEditingPackage(pkg);
    setIsModalOpen(true);
  };

  const handleToggleActive = async (pkg: Package) => {
    try {
      await packageService.toggleActive(pkg.id, !pkg.isActive);
      toast.success(`${pkg.name} ${pkg.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchPackages();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update package status');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const response = await packageService.delete(deleteTarget.id);
      if (response.success) {
        toast.success(`${deleteTarget.name} deleted successfully`);
        setDeleteTarget(null);
        fetchPackages();
      } else {
        toast.error(response.message || 'Failed to delete package');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete package');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalSuccess = () => {
    fetchPackages();
  };

  const activeCount = packages.filter(p => p.isActive).length;

  return (
    <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50">
      <Header searchPlaceholder="Search packages..." />

      <div className="flex-1 overflow-y-auto">
        {/* Page Header */}
        <div className="p-8 border-b border-slate-200 bg-white backdrop-blur-md">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-1">Subscription Packages</h1>
              <p className="text-slate-500 text-sm font-medium max-w-lg">
                Define and manage your service tiers, pricing, and resource limits for the entire platform.
              </p>
            </div>
            <button
              onClick={handleCreateClick}
              className="bg-primary hover:bg-primary/90 text-white px-5 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-transform active:scale-95 shadow-lg shadow-primary/20"
            >
              <span className="material-symbols-outlined text-lg">add</span>
              Create New Package
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="max-w-7xl mx-auto px-8 pt-6">
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-500 text-sm font-medium flex items-center gap-3">
              <span className="material-symbols-outlined">error</span>
              {error}
              <button onClick={fetchPackages} className="ml-auto text-red-500 hover:text-red-600 underline text-xs">
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <section className="p-8 max-w-7xl mx-auto w-full">
          {isLoading ? (
            <div className="flex items-center justify-center py-24">
              <div className="flex items-center gap-3 text-slate-500">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium">Loading packages...</span>
              </div>
            </div>
          ) : packages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="size-20 bg-slate-100 rounded-full flex items-center justify-center mb-4 border border-slate-200 text-slate-500">
                <span className="material-symbols-outlined text-4xl">inventory_2</span>
              </div>
              <h3 className="text-xl font-bold mb-2 text-slate-900">No packages yet</h3>
              <p className="text-slate-500 text-sm mb-6 max-w-sm">
                Create your first subscription package to start managing service tiers and pricing.
              </p>
              <button
                onClick={handleCreateClick}
                className="bg-primary hover:bg-primary/90 text-white px-6 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-transform active:scale-95"
              >
                <span className="material-symbols-outlined text-lg">add</span>
                Create First Package
              </button>
            </div>
          ) : (
            <>
              {/* Packages Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-start">
                {packages.map((pkg) => (
                  <PackageCard
                    key={pkg.id}
                    pkg={pkg}
                    onEdit={handleEditClick}
                    onToggleActive={handleToggleActive}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </div>

              {/* Add More */}
              <div className="mt-8">
                <button
                  onClick={handleCreateClick}
                  className="w-full border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-primary/30 transition-colors group"
                >
                  <div className="size-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 border border-slate-200 text-slate-500 group-hover:text-primary transition-colors">
                    <span className="material-symbols-outlined text-2xl">add_box</span>
                  </div>
                  <span className="text-primary font-bold text-sm">+ Add Another Package</span>
                </button>
              </div>
            </>
          )}
        </section>

        {/* Footer Stats */}
        {packages.length > 0 && (
          <footer className="mt-auto p-6 bg-white border-t border-slate-200">
            <div className="max-w-7xl mx-auto flex flex-wrap gap-8 items-center text-xs">
              <div className="flex items-center gap-2">
                <span className="text-slate-500 uppercase font-black">Total Packages:</span>
                <span className="text-slate-900 font-bold">{packages.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 uppercase font-black">Active:</span>
                <span className="text-primary font-bold">{activeCount}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 uppercase font-black">Inactive:</span>
                <span className="text-amber-500 font-bold">{packages.length - activeCount}</span>
              </div>
            </div>
          </footer>
        )}
      </div>

      {/* Create / Edit Modal */}
      <CreateEditPackageModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingPackage(null); }}
        onSuccess={handleModalSuccess}
        editPackage={editingPackage}
      />

      {/* Delete Confirmation */}
      {deleteTarget && (
        <DeleteConfirm
          pkg={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          isDeleting={isDeleting}
        />
      )}
    </main>
  );
};

export default PackagesPage;
